import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DChip, Pane, PaneHead } from '@/components/kit';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { errorText, useTechActions, useTechTask, type CantReason, type TechTask } from './api';
import { ageSex, CANT_REASONS, drName, eyeText, sendsTo, shortName } from './format';
import { isClosed } from './model';
import { CantModal } from './components/CantModal';
import { NoteField } from './components/NoteField';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

/**
 * Technician · Can't — `g_cantb`, phone `g_t5`.
 *
 * The capture's "Can't — reason ▾" opens its own surface: the signed order and
 * the patient on the left, where the reason GOES on the right (always the same
 * three destinations), and the picker over both.
 */
export function TechCantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useTechTask(id);
  const t = q.data;
  const [picking, setPicking] = useState(true);
  /* The phone draws Can't as its own screen (g_t5); the desk's picker is a
     modal over this page, which stays mounted under the phone layout. */
  const narrow = useNarrowViewport();
  usePageMeta({ title: 'Can’t' });
  const ov = useTechOverlays();
  const back = () => navigate(t ? `/technician/worklist/${t.id}` : '/technician/worklist');
  const done = () => navigate('/technician/worklist');
  const dr = t ? (drName(t.requestedBy) || sendsTo(t)) : '';
  const tries = t ? t.captures.length : 0;

  useShellChrome(
    {
      raw: true,
      title: t ? `Can’t — ${t.kindLabel}` : 'Can’t',
      sub: t ? (
        <>
          <button type="button" className="lnk" style={{ fontWeight: 600 }} onClick={back}>‹ Capture</button>
          {' ▸ '}{[t.title, shortName(t.patient), ageSex(t.patient)].filter(Boolean).join(' · ')}
        </>
      ) : undefined,
      search: 'Search orders, patients, MRN…',
      onSearch: ov.openSearch,
      onBell: ov.openBell,
      primary: t && !isClosed(t) ? { label: 'Can’t — reason ▾', onClick: () => setPicking(true) } : undefined,
      pane: t ? (
        <Pane
          head={<PaneHead overline="Where it goes · the screen’s own second half" title="Three destinations" sub="always the same three, for every reason" />}
          foot={<button type="button" className="btn btn-outline grow" onClick={back}>Back to the capture</button>}
        >
          <div className="card"><p className="t-body">1 · {dr} is <span className="w6">notified</span> — “1 test came back”</p></div>
          <div className="card"><p className="t-body">2 · An <span className="w6">attributed line</span> on the order — your name, the reason, the time</p></div>
          <div className="card"><p className="t-body">3 · The order <span className="w6">closes</span> — never silently</p></div>
          <p className="t-sec mut center mt16">“I told the doctor” stops being a corridor event</p>
          <div className="card">
            <p className="t-ovl mut">The third exit</p>
            <p className="t-sec sec mt8">
              Capture it, hand it over, or say you can’t. Can’t is a per-task control here — it sits on the row, on the order’s
              pane and on every capture, including Refraction.
            </p>
          </div>
        </Pane>
      ) : null,
      phone: t ? <PhoneCant t={t} onDone={done} onBack={back} /> : null,
    },
    [t?.id, t?.state, dr],
  );

  if (!t) return <div className="maie desk tech-root">
        {ov.overlays}<div className="dcontent" /></div>;

  return (
    <div className="maie desk tech-root">
        {ov.overlays}
      <div className="dcontent">
        <div className="card card-acc">
          <p className="t-body" style={{ color: 'var(--brand-600)' }}>
            <span className="w6">Signed order — {dr}:</span>{' '}
            {[t.title + (t.laterality ? `, ${eyeText(t.laterality)}` : ''), [t.priority !== 'routine' ? t.priority : '', t.visitOpen ? 'the visit is open' : ''].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="card card-flat" style={{ background: 'var(--info-100)' }}>
          <p className="t-strong">{[shortName(t.patient), ageSex(t.patient), t.patient?.mrn ? `MRN ${t.patient.mrn}` : ''].filter(Boolean).join(' · ')}</p>
          <p className="t-sec mut mt8">
            {tries ? `${tries} attempt${tries === 1 ? '' : 's'} attached` : 'no attempt attached'} · nothing sent
          </p>
        </div>
        {isClosed(t) ? (
          <div className="card">
            <p className="t-sec">This test is already closed{t.cantReason ? ' — it came back' : ''}.</p>
            <p className="mt8"><DChip kind="outline" onClick={done}>Back to the worklist</DChip></p>
          </div>
        ) : null}
        <p className="t-sec sec mt16">
          Nothing is performed and nothing is billed until the reason is chosen — the task closes either way, never silently.
        </p>
        <p className="t-sec sec mt16">
          The list is the same in every specialty and always ends with “Other — write the reason”. Pick <span className="w6">Machine down</span> and
          the picker hands you the device toggle: one documented state change instead of twenty individual failures.
        </p>
      </div>
      {!isClosed(t) ? (
        <CantModal task={t} open={picking && !narrow} onClose={() => setPicking(false)} onDone={done} />
      ) : null}
    </div>
  );
}

/* ─────────────────────────── phone · g_t5 ─────────────────────────── */

function PhoneCant({ t, onDone, onBack }: { t: TechTask; onDone: () => void; onBack: () => void }) {
  const act = useTechActions();
  const [reason, setReason] = useState<CantReason | null>(null);
  const [note, setNote] = useState('');
  const dr = drName(t.requestedBy) || sendsTo(t);
  const send = () => {
    if (!reason) return;
    act.cant.mutate({ id: t.id, reason, note: note.trim() || undefined }, {
      onSuccess: () => {
        toast.success(`Sent back to ${dr}`);
        onDone();
      },
      onError: (e) => toast.error(errorText(e)),
    });
  };
  return (
    <PhoneScreen door="worklist" crumb={`${t.title} · ${shortName(t.patient)}`} title="Can’t" onBack={onBack}>
      <p className="t-sec mut mb8">Why not?</p>
      {CANT_REASONS.map((r) => {
        const on = reason === r.key;
        return (
          <button key={r.key} type="button" className="card card-btn" onClick={() => setReason(r.key)}
            style={on ? { borderColor: r.key === 'machine_down' ? 'var(--danger-600)' : 'var(--brand-500)' } : undefined}
            aria-pressed={on}>
            <p className={`t-body${on ? ' w6' : ''}${on && r.key === 'machine_down' ? ' red' : ''}${r.key === 'other' && !on ? ' sec' : ''}`}>{r.phone}</p>
          </button>
        );
      })}
      <div className="card card-ghost">
        <p className="t-ovl mut">Note <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional, one line)</span></p>
        <p className="t-sec sec mt4">
          <NoteField label="Note" value={note} onChange={setNote} maxLength={500} placeholder="what happened, in one line" />
        </p>
      </div>
      <button type="button" className="btn mt12" style={{ background: 'var(--warning-600)', color: 'var(--surface-0)' }}
        disabled={!reason || (reason === 'other' && !note.trim()) || act.cant.isPending} onClick={send}>
        Send back to {dr}
      </button>
      <div className="card card-acc mt12">
        <p className="t-cap" style={{ color: 'var(--brand-600)', lineHeight: 1.6 }}>
          → {t.requestedBy ? drName(t.requestedBy) : 'the doctor'} is notified · an attributed line on the order · machine-down
          marks the machine and offers the desk’s outside request · the task closes, never silently
        </p>
      </div>
    </PhoneScreen>
  );
}

